/** @jsxImportSource @emotion/react */
import { Header, TruncatedString } from '@quickbit/backoffice-core-frontend';
import {
  Modal,
  Pagination,
  Table,
  Typography,
  UtilityButton,
  useTheme,
} from '@quickbit/qb-design-system';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAsync, useAsyncFn } from 'react-use';
import { deleteSettlementPayment, searchSettlementPayments } from '../api';

const HEADERS = [
  { label: 'Created at', cellKey: 'createdAt', sort: false },
  { label: 'Paid at', cellKey: 'paidAt', sort: false },
  { label: 'Id', cellKey: 'uuid', sort: false },
  { label: 'Type', cellKey: 'type', sort: false },
  { label: 'Merchant', cellKey: 'merchant', sort: false },
  { label: 'Amount', cellKey: 'amount', sort: false },
  { label: 'Report', cellKey: 'report', sort: false },
  { label: 'Comment', cellKey: 'comment', sort: false },
  { label: 'Delete', cellKey: 'delete', sort: false },
];

export const PaymentHistory = () => {
  const { spacing, palette } = useTheme();
  const [page, setPage] = useState(1);
  const navigate = useNavigate();
  const [{ value, loading }, refresh] = useAsyncFn(
    () => searchSettlementPayments({ pagination: { page, size: 16 } }),
    [page]
  );
  const [{ loading: deleteLoading }, deletePayment] = useAsyncFn(
    deleteSettlementPayment
  );
  const [paymentToBeDeletedId, setPaymentToBeDeletedId] = useState<
    string | null
  >(null);
  const handlePaymentDeleteClick = (uuid: string) => {
    setPaymentToBeDeletedId(uuid);
  };

  useAsync(() => {
    return refresh();
  }, [refresh]);

  const rows = useMemo(
    () =>
      value?.payments.map((payment) => {
        return {
          value: {
            uuid: <TruncatedString text={payment.uuid} />,
            type: payment.usdtAmount
              ? 'USDT'
              : payment.usdcAmount
              ? 'USDC'
              : 'EUR',
            merchant: payment.merchant.name,
            createdAt: payment.createdAt.toLocaleString(),
            paidAt: payment.paidAt.toLocaleString(),
            amount:
              payment.usdcAmount || payment.usdtAmount ? (
                <>
                  <Typography variant="bodySmall">
                    {payment.usdtAmount ?? payment.usdcAmount}
                  </Typography>
                  <Typography
                    variant="bodySmallest"
                    color={palette.grey.default}
                  >{`${payment.euroAmount} EUR`}</Typography>
                </>
              ) : (
                payment.euroAmount
              ),
            comment: payment.reference,
            delete: !payment.usdtAmount ? (
              <UtilityButton
                text="Delete"
                disabled={payment.connectedToProcessedReport}
                onClick={() => handlePaymentDeleteClick(payment.uuid)}
              />
            ) : null,
            report: payment.reportId ? (
              <Link to={`/accounting/reports/${payment.reportId}`}>View</Link>
            ) : (
              ''
            ),
          },
        };
      }),
    [value, palette]
  );

  return (
    <>
      <Header>
        {' '}
        <div
          css={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div css={{ display: 'flex', gap: spacing.xxs }}>
            <Typography variant="subtitle">Payments</Typography>
          </div>
          <div>
            <UtilityButton
              css={{ marginRight: spacing.xxs }}
              text="Create Payment"
              onClick={() => navigate('create')}
            />
            <UtilityButton
              text="Refresh"
              onClick={refresh}
              variant="secondary"
              loading={loading}
            />
          </div>
        </div>
      </Header>
      {value && (
        <>
          <div css={{ marginTop: spacing.xxs }}>
            <Table headers={HEADERS} rows={rows ?? []} />
          </div>
          <div css={{ marginTop: spacing.xxs }}>
            <Pagination
              currentPage={page}
              onChange={setPage}
              numberOfElements={value.aggregateData.count}
              pageSize={16}
            />
          </div>
        </>
      )}
      {paymentToBeDeletedId && (
        <Modal
          closeModal={() => setPaymentToBeDeletedId(null)}
          primaryButtonProps={{
            text: 'Delete',
            loading: deleteLoading,
            onClick: async () => {
              try {
                await deletePayment(paymentToBeDeletedId);
                setPaymentToBeDeletedId(null);
                void refresh();
              } catch (error) {
                console.error('Error deleting payment:', error);
              }
            },
          }}
          secondaryButtonProps={{
            text: 'Close',
            onClick: () => setPaymentToBeDeletedId(null),
          }}
        >
          Are you sure you want to delete payment with id {paymentToBeDeletedId}
          ?
        </Modal>
      )}
    </>
  );
};
